import { useState, useEffect, useRef, useMemo } from "react";
import { ChevronUp } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import { cn } from "@/utils/css";
import { TranslatedText } from "@/components/common/text";
import { fetchAllOffers, clearChangedCounts, clearOffersState } from "@/state/offersSlice";
import { MyOfferCard, MyOfferEmpty } from "@/components/dataPages/loadboard";
import { OffersRefreshProvider } from "@/contexts/OffersRefreshContext";
import { useOfferStatusNotifications } from "@/hooks/useOfferStatusNotifications";
import { ClickOutside } from "@/components/common/other/ClickOutside";
import dayjs from "dayjs";

export const MyOffers = () => {
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("pending");
    const { quotes, bookings, changedCounts } = useSelector((state) => state.offers);
    const intervalRef = useRef(null);

    // Show toast notifications for status changes
    useOfferStatusNotifications(quotes.data, bookings.data);

    const loadOffers = (isAutoRefresh = false) => {
        dispatch(fetchAllOffers({ isAutoRefresh }));
    };

    const resetInterval = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        intervalRef.current = setInterval(() => {
            loadOffers(true);
        }, 60000);
    };

    useEffect(() => {
        // Initial fetch (not auto refresh)
        loadOffers(false);

        // Set up interval for auto refresh
        resetInterval();

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            dispatch(clearOffersState());
        };
    }, []);

    // Clear changed counts when switching tabs
    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        // Clear count for the tab being viewed (if it has a count)
        if (tabId !== "history" && changedCounts[tabId] > 0) {
            dispatch(clearChangedCounts(tabId));
        }
    };

    // Clear changed counts for active tab when clicking on offers
    const handleOfferClick = () => {
        if (activeTab !== "history") {
            dispatch(clearChangedCounts(activeTab));
        }
    };

    const allOffers = useMemo(() => {
        return [
            ...(quotes.data || []).map((quote) => ({ ...quote, type: "quote" })),
            ...(bookings.data || []).map((booking) => ({ ...booking, type: "booking" })),
        ].sort((a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf());
    }, [quotes.data, bookings.data]);

    const pendingOffers = useMemo(() => {
        return [
            ...(quotes.data || [])
                .filter((quote) => ["PENDING", "NOT_SUBMITTED"].includes(quote.status))
                .map((quote) => ({ ...quote, type: "quote" })),
            ...(bookings.data || [])
                .filter((booking) => ["PENDING", "NOT_SUBMITTED"].includes(booking.status))
                .map((booking) => ({ ...booking, type: "booking" })),
        ].sort((a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf());
    }, [quotes.data, bookings.data]);

    const acceptedOffers = useMemo(() => {
        return [
            ...(quotes.data || []).filter((quote) => quote.status === "ACCEPTED").map((quote) => ({ ...quote, type: "quote" })),
            ...(bookings.data || [])
                .filter((booking) => booking.status === "CONFIRMED")
                .map((booking) => ({ ...booking, type: "booking" })),
        ].sort((a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf());
    }, [quotes.data, bookings.data]);

    const declinedOffers = useMemo(() => {
        return [
            ...(quotes.data || [])
                .filter((quote) => ["DECLINED", "CANCELLED", "EXPIRED"].includes(quote.status))
                .map((quote) => ({ ...quote, type: "quote" })),
            ...(bookings.data || [])
                .filter((booking) => ["REJECTED", "CANCELLED"].includes(booking.status))
                .map((booking) => ({ ...booking, type: "booking" })),
        ].sort((a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf());
    }, [quotes.data, bookings.data]);

    const tabs = [
        { id: "pending", label: "Pending", count: pendingOffers.length, changed: changedCounts.pending },
        { id: "accepted", label: "Accepted", count: acceptedOffers.length, changed: changedCounts.accepted },
        { id: "declined", label: "Declined", count: declinedOffers.length, changed: changedCounts.declined },
        { id: "history", label: "History", count: allOffers.length },
    ];

    const getActiveOffers = () => {
        switch (activeTab) {
            case "pending":
                return pendingOffers;
            case "accepted":
                return acceptedOffers;
            case "declined":
                return declinedOffers;
            case "history":
                return allOffers;
            default:
                return pendingOffers;
        }
    };

    const activeOffers = getActiveOffers();

    const totalNotifications = changedCounts.pending + changedCounts.accepted + changedCounts.declined;
    const hasNotifications = totalNotifications > 0;

    const handleClickAway = () => {
        setIsOpen(false);
    };

    return (
        <OffersRefreshProvider value={resetInterval}>
            <ClickOutside onClick={handleClickAway}>
                <div
                    className={cn(
                        "w-[172px] h-12 fixed right-8 bottom-6 transition-all overflow-hidden rounded-lg flex flex-col border border-cm-p-outline z-20",
                        {
                            "w-[600px] h-[489px]": isOpen,
                        },
                    )}
                >
                    <div
                        className={cn(
                            "flex items-center justify-between transition-colors py-3 pl-4 pr-2 bg-cm-p-surface-container text-cm-p-text-primary font-medium",
                            {
                                "bg-cm-p-on-container-green text-cm-p-surface": isOpen || hasNotifications,
                            },
                        )}
                    >
                        <div className="flex items-center gap-2 text-sm/[24px]">
                            <TranslatedText>My offers</TranslatedText>
                            {hasNotifications && (
                                <span className="bg-cm-p-container-green text-cm-p-on-container-green rounded-full text-sm/[24px] font-medium size-6 text-center">
                                    {totalNotifications}
                                </span>
                            )}
                        </div>
                        <button type="button" onClick={() => setIsOpen(!isOpen)}>
                            <ChevronUp
                                className={cn("w-6 transition ease-in", {
                                    "rotate-180": isOpen,
                                })}
                            />
                        </button>
                    </div>
                    <div className="flex flex-col flex-1 bg-cm-p-surface overflow-hidden p-1 pb-0">
                        <div className="flex gap-1">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabChange(tab.id)}
                                    className={cn(
                                        "w-1/4 px-3 py-2 text-sm font-medium transition-colors bg-cm-p-surface-container border-[2px] border-cm-p-surface-container border-b-0 inline-flex items-center justify-start gap-1",
                                        {
                                            "bg-cm-p-surface": activeTab === tab.id,
                                        },
                                    )}
                                >
                                    <TranslatedText>{tab.label}</TranslatedText>
                                    {tab.changed !== undefined && tab.changed > 0 && (
                                        <span className="bg-cm-p-on-container-green text-cm-p-container-green rounded-full px-1.5 py-0.5 text-xs font-medium min-w-[20px] text-center">
                                            {tab.changed}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>

                        <div className="flex-1 px-1 pt-1 overflow-y-auto">
                            {quotes.loading || bookings.loading ? (
                                <div className="w-full h-full flex items-center justify-center text-cm-p-text-secondary">
                                    <TranslatedText>Loading...</TranslatedText>
                                </div>
                            ) : activeOffers.length === 0 ? (
                                <MyOfferEmpty tab={activeTab} />
                            ) : (
                                <div className="w-full flex flex-col gap-1">
                                    {activeOffers.map((offer, index) => (
                                        <MyOfferCard offer={offer} index={index} key={offer.id} onInteract={handleOfferClick} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </ClickOutside>
        </OffersRefreshProvider>
    );
};
